import type { ComponentProps, KeyboardEvent, ReactNode } from 'react';
import { Icon } from '../components';
import { Link } from '../router';
import './workspace-primitives.css';

export type WorkspaceTone = 'neutral' | 'primary' | 'info' | 'success' | 'warning' | 'danger';
type IconName = ComponentProps<typeof Icon>['name'];


export function activateWorkspaceRow(event: KeyboardEvent<HTMLElement>, onActivate: () => void) {
  const target = event.target as HTMLElement;
  if (target !== event.currentTarget && target.closest('a,button,input,select,textarea,[role="button"],[role="link"]')) return;
  if (event.key !== 'Enter' && event.key !== ' ') return;
  event.preventDefault();
  onActivate();
}

export function WorkspacePageHeader({ title, description, icon, actions }: { title: string; description?: string; icon?: IconName; actions?: ReactNode }) {
  return <header className="nds-workspace-page-header">
    {icon ? <span className="nds-workspace-page-icon"><Icon name={icon} /></span> : null}
    <span className="nds-workspace-page-copy"><h1>{title}</h1>{description ? <p>{description}</p> : null}</span>
    {actions ? <span className="nds-workspace-page-header-actions">{actions}</span> : null}
  </header>;
}

export function WorkspaceMetricCard({ to, icon, label, value, detail, tone = 'neutral' }: { to?: string; icon: IconName; label: string; value: ReactNode; detail: string; tone?: WorkspaceTone }) {
  const content = <><span className={`nds-workspace-metric-icon is-${tone}`}><Icon name={icon} /></span><span className="nds-workspace-metric-copy"><small>{label}</small><strong>{value}</strong><em>{detail}</em></span></>;
  return to ? <Link className="nds-workspace-metric" to={to}>{content}</Link> : <div className="nds-workspace-metric">{content}</div>;
}

export function WorkspacePanelHeader({ icon, title, description, action }: { icon?: IconName; title: string; description?: string; action?: ReactNode }) {
  return <header className="nds-workspace-panel-header">
    <span className="nds-workspace-panel-title">{icon ? <span className="nds-workspace-panel-icon"><Icon name={icon} /></span> : null}<span><h2>{title}</h2>{description ? <p>{description}</p> : null}</span></span>
    {action ? <span className="nds-workspace-panel-action">{action}</span> : null}
  </header>;
}


export function WorkspaceActionCard({ icon, label, detail, to, onClick, tone = 'neutral' }: { icon: IconName; label: string; detail?: string; to?: string; onClick?: () => void; tone?: WorkspaceTone }) {
  const content = <><span className={`nds-action-card-icon is-${tone}`}><Icon name={icon} /></span><span className="nds-action-card-copy"><strong>{label}</strong>{detail ? <small>{detail}</small> : null}</span></>;
  if (to) return <Link className="nds-action-card" to={to}>{content}</Link>;
  return <button type="button" className="nds-action-card" onClick={onClick}>{content}</button>;
}

export function WorkspaceSummaryList({ children, ariaLabel }: { children: ReactNode; ariaLabel?: string }) {
  return <div className="nds-summary-list" aria-label={ariaLabel}>{children}</div>;
}

export type WorkspaceBarItem = {
  key: string;
  label: string;
  value: number;
  detail?: ReactNode;
  tone?: WorkspaceTone;
};

export function WorkspaceBarSummary({ items, maxValue, valueFormatter = (value) => String(value), ariaLabel }: { items: WorkspaceBarItem[]; maxValue?: number; valueFormatter?: (value: number) => ReactNode; ariaLabel?: string }) {
  const ceiling = Math.max(1, maxValue ?? 0, ...items.map((item) => Math.max(0, Number(item.value || 0))));
  return <div className="nds-bar-summary" aria-label={ariaLabel}>{items.map((item) => {
    const value = Math.max(0, Number(item.value || 0));
    const percent = Math.max(0, Math.min(100, (value / ceiling) * 100));
    return <div className="nds-bar-summary-row" key={item.key}>
      <div className="nds-bar-summary-meta"><span><strong>{item.label}</strong>{item.detail ? <small>{item.detail}</small> : null}</span><b>{valueFormatter(value)}</b></div>
      <div className="nds-bar-summary-track"><span className={`nds-bar-summary-fill is-${item.tone || 'primary'}`} style={{ width: `${percent}%` }} /></div>
    </div>;
  })}</div>;
}

export function WorkspaceEndMeta({ status, primary, secondary }: { status?: ReactNode; primary?: ReactNode; secondary?: ReactNode }) {
  return <span className="nds-workspace-end-meta">{status}<span className="nds-workspace-end-meta-copy">{primary ? <strong>{primary}</strong> : null}{secondary ? <small>{secondary}</small> : null}</span></span>;
}

export function WorkspaceStatusIcon({ icon, label, tone = 'neutral', size = 'md' }: { icon: IconName; label: string; tone?: WorkspaceTone; size?: 'sm' | 'md' }) {
  return <span className={`nds-workspace-status-icon is-${tone} is-${size}`} title={label} aria-label={label}><Icon name={icon} /></span>;
}
